// The owner's first setup in five steps (organization, venue and city, opening hours, number mode, languages), and the
// same form minus the first step when an owner adds a venue. Each step is validated with the rules of
// @ringring/core/venue before moving on; the API validates again and answers with the same codes.
import { useState, type FormEvent } from 'react';
import {
  LANG_DIR,
  LANG_LABELS,
  SETUP_STEPS,
  SUPPORTED_LANGS,
  UI,
  cleanName,
  hasOpeningHours,
  parseLanguages,
  parseOpeningHours,
  pickDefaultLang,
  type Lang,
  type NumberMode,
  type SetupStep,
  type UiCopy,
} from '@ringring/core';
import { HoursEditor } from './HoursEditor';
import { api, errorText, type ApiError, type Venue, type VenueSetupBody } from './api';
import { daysFromHours, hoursFromDays, type DayHours } from './hours';
import { Filled, LangPicker, Logo } from './lang';

/** The codes a step's own check can find; the API answers the same codes, and both are worded by `errorText`. */
type ErrorKey = 'bad_org_name' | 'bad_venue_name' | 'bad_city' | 'bad_hours' | 'bad_languages';

export interface SetupProps {
  lang: Lang;
  setLang: (l: Lang) => void;
  /** Set when an owner adds a venue to an existing organization: the organization step is skipped. */
  orgId?: string | null;
}

export function Setup({ lang, setLang, orgId = null }: SetupProps) {
  const t = UI[lang];
  const steps: readonly SetupStep[] = orgId ? SETUP_STEPS.filter((s) => s !== 'organization') : SETUP_STEPS;
  const [index, setIndex] = useState(0);
  const [org, setOrg] = useState('');
  const [venue, setVenue] = useState('');
  const [city, setCity] = useState('');
  const [days, setDays] = useState<DayHours[]>(() => daysFromHours(null));
  const [mode, setMode] = useState<NumberMode>('scan');
  const [langs, setLangs] = useState<Lang[]>([...SUPPORTED_LANGS]);
  const [defaultLang, setDefaultLang] = useState<Lang>(lang);
  /** The code of the first problem (a local check or the API's refusal), worded at render time in the screen language. */
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const step = steps[index];
  const last = index === steps.length - 1;

  /** The copy key of the first problem on the current step, or null when it is complete. */
  const check = (s: SetupStep): ErrorKey | null => {
    switch (s) {
      case 'organization':
        return cleanName(org) ? null : 'bad_org_name';
      case 'venue':
        return !cleanName(venue) ? 'bad_venue_name' : !cleanName(city) ? 'bad_city' : null;
      case 'hours': {
        const h = parseOpeningHours(hoursFromDays(days));
        return h && hasOpeningHours(h) ? null : 'bad_hours';
      }
      case 'mode':
        return null;
      case 'languages':
        return parseLanguages(langs) ? null : 'bad_languages';
    }
  };

  const body = (): VenueSetupBody => {
    const languages = parseLanguages(langs) ?? [lang];
    return { name: venue, city, opening_hours: hoursFromDays(days), number_mode: mode, languages, default_lang: pickDefaultLang(languages, defaultLang) };
  };

  const submit = async () => {
    setBusy(true);
    try {
      const r = orgId
        ? await api<{ venue: Venue }>(`/account/organizations/${encodeURIComponent(orgId)}/venues`, { method: 'POST', body: JSON.stringify(body()) })
        : await api<{ venue: Venue }>('/account/organizations', { method: 'POST', body: JSON.stringify({ name: org, venue: body() }) });
      try {
        localStorage.setItem('rr_venue', r.venue.id);
      } catch {
        // storage unavailable
      }
      window.location.hash = `#/dashboard/${encodeURIComponent(r.venue.id)}`;
    } catch (e) {
      const err = e as ApiError;
      if (err.status === 401) {
        window.location.hash = '#/signin';
        return;
      }
      setProblem(err.code ?? 'internal');
    } finally {
      setBusy(false);
    }
  };

  const next = (e: FormEvent) => {
    e.preventDefault();
    const bad = check(step);
    if (bad) {
      setProblem(bad);
      return;
    }
    setProblem(null);
    if (last) void submit();
    else setIndex(index + 1);
  };

  const back = () => {
    setProblem(null);
    setIndex(Math.max(0, index - 1));
  };

  const toggleLang = (l: Lang) => {
    const on = langs.includes(l);
    setLangs(on ? langs.filter((x) => x !== l) : SUPPORTED_LANGS.filter((x) => x === l || langs.includes(x)));
  };

  const field = (id: string, label: string, hint: string, value: string, set: (v: string) => void, extra: Record<string, unknown> = {}) => (
    <>
      <label htmlFor={id}>{label}</label>
      <input id={id} value={value} onChange={(e) => set(e.target.value)} maxLength={80} autoComplete="off" aria-describedby={`${id}-hint`} required {...extra} />
      <p id={`${id}-hint`} className="muted">
        {hint}
      </p>
    </>
  );

  return (
    <div className="app setup" lang={lang} dir={LANG_DIR[lang]}>
      <div className="topbar">
        <a href="#/">
          <Logo />
        </a>
        <LangPicker lang={lang} setLang={setLang} />
      </div>
      <div>
        <h1 className="display">{orgId ? t.add_venue_title : t.setup_title}</h1>
        {orgId ? null : <p className="muted">{t.setup_intro}</p>}
      </div>
      <p className="step-count" aria-live="polite">
        <Filled template={t.step_of} vars={{ n: String(index + 1), total: String(steps.length) }} />
      </p>
      <ol className="steps">
        {steps.map((s, i) => (
          <li key={s} aria-current={i === index ? 'step' : undefined} className={i < index ? 'done' : ''}>
            <span aria-hidden="true">{i < index ? '✓' : <bdi dir="ltr">{i + 1}</bdi>}</span> {t[`step_${s}` as keyof UiCopy]}
          </li>
        ))}
      </ol>
      <form className="card" onSubmit={next} noValidate>
        {step === 'organization' ? field('org', t.org_name, t.org_name_hint, org, setOrg, { autoFocus: true }) : null}
        {step === 'venue' ? (
          <>
            {field('venue', t.venue_name, t.venue_name_hint, venue, setVenue, { autoFocus: true })}
            {field('city', t.city, t.city_hint, city, setCity, { autoComplete: 'address-level2' })}
          </>
        ) : null}
        {step === 'hours' ? (
          <>
            <p className="muted">{t.hours_intro}</p>
            <HoursEditor lang={lang} days={days} onChange={setDays} idPrefix="setup-hours" />
          </>
        ) : null}
        {step === 'mode' ? (
          <fieldset className="choices">
            <legend>{t.mode_prompt}</legend>
            {(['scan', 'ticket'] as const).map((m) => (
              <label className={`choice ${mode === m ? 'selected' : ''}`} key={m}>
                <input type="radio" name="mode" value={m} checked={mode === m} onChange={() => setMode(m)} />
                <span className="choice-title">
                  <span aria-hidden="true">{mode === m ? '● ' : '○ '}</span>
                  {m === 'scan' ? t.mode_scan : t.mode_ticket}
                </span>
                <span className="muted">{m === 'scan' ? t.mode_scan_desc : t.mode_ticket_desc}</span>
              </label>
            ))}
          </fieldset>
        ) : null}
        {step === 'languages' ? (
          <>
            <fieldset className="choices inline">
              <legend>{t.languages_prompt}</legend>
              {SUPPORTED_LANGS.map((l) => (
                <label className="check" key={l} lang={l}>
                  <input type="checkbox" checked={langs.includes(l)} onChange={() => toggleLang(l)} />
                  <span>{LANG_LABELS[l]}</span>
                </label>
              ))}
            </fieldset>
            <p className="muted">{t.languages_hint}</p>
            <fieldset className="choices inline">
              <legend>{t.default_lang}</legend>
              {langs.map((l) => (
                <label className="check" key={l} lang={l}>
                  <input type="radio" name="default_lang" value={l} checked={pickDefaultLang(langs, defaultLang) === l} onChange={() => setDefaultLang(l)} />
                  <span>{LANG_LABELS[l]}</span>
                </label>
              ))}
            </fieldset>
          </>
        ) : null}
        {problem ? (
          <p id="setup-problem" role="alert">
            {errorText(lang, problem)}
          </p>
        ) : null}
        <div className="row">
          {index > 0 ? (
            <button className="btn secondary" type="button" onClick={back} disabled={busy}>
              {t.back}
            </button>
          ) : null}
          <span className="spacer" />
          <button className="btn" type="submit" disabled={busy}>
            {busy ? t.saving : last ? t.finish : t.next}
          </button>
        </div>
      </form>
    </div>
  );
}
